import { Injectable } from '@angular/core';
import { FabricComposition, YarnDetail } from '../models/greige/greige';
import { Yarn } from '../models/yarns/yarn';

export interface RevolutionMetrics {
  metersPerRev: number;
  kgPerRev: number;
  lbsPerRev: number;
  loopsPerNeedle: number;
}

export interface YarnDetailExtended extends YarnDetail {
  selectedYarn: Yarn;
}

@Injectable({
  providedIn: 'root',
})
export class YarnCalculationsService {
  // Global constants (you can adjust these as needed)
  private readonly LOOP_LENGTH_CONVERSION_FACTOR = 0.0001; // Converts the provided loopLength to meters
  private readonly WEIGHT_FACTOR = 0.59; // Global constant factor
  private readonly KG_TO_LBS = 2.2046; // Conversion factor from Kg to Lbs

  /**
   * Calculates the metrics per revolution.
   *
   * @param needles - The number of needles (e.g., 2688)
   * @param loopLength - The loop length value (e.g., 4) that will be converted using LOOP_LENGTH_CONVERSION_FACTOR
   * @param cones - Number of cones (e.g., 35)
   * @param count - Count value (e.g., 32)
   * @returns An object containing metersPerRev, kgPerRev, and lbsPerRev
   */
  public calculateRevolutionMetrics(needles: number, loopLength: number, cones: number, count: number): RevolutionMetrics {
    // Convert loopLength to meters using the conversion factor.
    // (e.g., 4 becomes 0.0004)
    const loopLengthInMeters = loopLength * this.LOOP_LENGTH_CONVERSION_FACTOR;

    // Calculate meters per revolution.
    // For example: 0.0004 * 2688 ≈ 1.0752 (≈1.08)
    const metersPerRev = loopLengthInMeters * needles;

    // Calculate Kg per revolution.
    // Using the formula: (metersPerRev * WEIGHT_FACTOR * (cones / count)) / 1000
    // For example: (1.0752 * 0.59 * (35 / 32)) / 1000 ≈ 0.00069 (≈0.0007)
    const kgPerRev = (metersPerRev * this.WEIGHT_FACTOR * (cones / count)) / 1000;

    // Calculate Lbs per revolution.
    // For example: 0.00069 * 2.2046 ≈ 0.00152 (≈0.0015)
    const lbsPerRev = kgPerRev * this.KG_TO_LBS;

    // Calculate Loops per Needle
    const loopsPerNeedle = loopLength / 50;

    // Round all values to 4 decimal places.
    return {
      metersPerRev: Math.round(metersPerRev * 10000) / 10000,
      kgPerRev: Math.round(kgPerRev * 10000) / 10000,
      lbsPerRev: Math.round(lbsPerRev * 10000) / 10000,
      loopsPerNeedle: Math.round(loopsPerNeedle * 10000) / 10000,
    };
  }

  getFabricComposition(yarnDetails: YarnDetailExtended[]): FabricComposition[] {
    if (!yarnDetails.every((yd) => yd.usePercentage != null)) {
      return [];
    }

    const compositionMap: Record<string, number> = {};

    yarnDetails.forEach((yd) => {
      const usePercentage = yd.usePercentage!;
      // For each yarn composition component in the selected yarn...
      yd.selectedYarn?.yarnComposition?.forEach((comp) => {
        // Calculate the effective contribution from this yarn detail.
        // For example, if comp.percentage is 70 and usePercentage is 50,
        // effectivePercentage will be (70 * 50) / 100 = 35.
        const effectivePercentage = (comp.percentage * usePercentage) / 100;

        // Accumulate the contribution by compositionCode.
        if (compositionMap[comp.compositionCode]) {
          compositionMap[comp.compositionCode] += effectivePercentage;
        } else {
          compositionMap[comp.compositionCode] = effectivePercentage;
        }
      });
    });

    // Convert the compositionMap into an array of FabricComposition objects.
    return Object.entries(compositionMap).map(([compositionCode, percentage]) => ({
      compositionCode,
      percentage,
    }));
  }
}
